﻿<script lang="ts">
    import { Button, Icon, Treeview, TreeviewItem } from 'framework7-svelte';
    import { createEventDispatcher } from 'svelte';
    import { _ } from 'svelte-i18n';
    
    import FloatingButton from '../FloatingButton.svelte';
    import DatePicker from '../DatePicker.svelte';
    import {datesAreEqual, formatDateString, formatMomentTime, toDateOnly} from "../../helpers/date.helper";
    import type {MeasurementMoment} from "../../types/measurement-moment.type";
    import {measurementMomentsStore} from "../../stores/measurement-moments.store";
    import {measurementMomentExpandedStore} from "../../stores/measurement-moment-expanded.store";
    import {clamp} from "../../helpers/statistics.helper";
    
    interface ITimestampOption {
        label: string;
        timestamps: Date[];
        measurementIds: string[];
        opened?: boolean;
    }
    
    const eventDispatcher = createEventDispatcher();
    
    $: timestampOptions = setTimestampOptions($measurementMomentsStore.data);
    $: if ($measurementMomentExpandedStore.data) {
        updateSteps();
    }

    let steps: { [key: string]: boolean } = {
        '-20': false,
        '-5': false,
        '-1': false,
        '+1': false,
        '+5': false,
        '+20': false
    };

    function setTimestampOptions(measurementTimestamps: MeasurementMoment[]): {[key: string]: ITimestampOption[]} {
        const newTimestamps: {[key: string]: ITimestampOption[]} = {};
        if(measurementTimestamps === null) return newTimestamps;
                
        measurementTimestamps.map((measurementTimestamp) => {
            const dayString = formatDateString(toDateOnly(measurementTimestamp.moment));
            const hourLabel = measurementTimestamp.moment.toLocaleTimeString().split(':')[0] + ':00';
            if(!newTimestamps[dayString]) {
                newTimestamps[dayString] = [];
            }
            
            const existingOption = newTimestamps[dayString]?.find((option) => option.label === hourLabel);
            if (!existingOption) {
                newTimestamps[dayString].push({
                    label: hourLabel,
                    timestamps: [measurementTimestamp.moment],
                    measurementIds: [measurementTimestamp.id]
                });
            }
            else {
                existingOption.timestamps.push(measurementTimestamp.moment);
                existingOption.measurementIds.push(measurementTimestamp.id);
            }
        });

        return newTimestamps;
    }

    function incrementTimestamp(amount: number) {
        const currentIndex = $measurementMomentsStore.data.findIndex(
            (measurementMoment) => datesAreEqual(measurementMoment.moment, $measurementMomentExpandedStore.data.moment)
        );

        let newIndex = currentIndex + amount;
        newIndex = clamp(newIndex, 0, $measurementMomentsStore.data.length - 1);

        const selected = $measurementMomentsStore.data[newIndex];
        eventDispatcher('timestampSelected', selected.id);
    }

    function updateSteps() {
        const currentIndex = $measurementMomentsStore.data.indexOf($measurementMomentsStore.data.find(
            (measurementMoment) => datesAreEqual(measurementMoment.moment, $measurementMomentExpandedStore.data.moment)
        ));

        steps = Object.fromEntries(
            Object.keys(steps).map((key) => {
                const newIndex = currentIndex + parseInt(key);
                return [key, newIndex >= 0 && newIndex < $measurementMomentsStore.data.length];
            })
        );
    }
    
    function selectMeasurementMoment(timestamp: Date) {
        const selected = $measurementMomentsStore.data.find(
            (measurementMoment) =>  datesAreEqual(measurementMoment.moment, timestamp)
        );
        
        eventDispatcher('timestampSelected', selected.id);
    }
    
    function selectDate(date: Date) {
        const selected = $measurementMomentsStore.data.find(
            (measurementMoment) => datesAreEqual(toDateOnly(measurementMoment.moment), date)
        );

        eventDispatcher('timestampSelected', selected.id);
    }
    
    function openSection(section: ITimestampOption) {
        section.opened = true
    }
</script>

<FloatingButton
    label={$measurementMomentExpandedStore.data ? formatDateString($measurementMomentExpandedStore.data.moment) : $_('pages.project.NO_MEASUREMENTS_AVAILABLE')}
    disabled={$measurementMomentsStore.data?.length == 0}
    active={false}
    inactiveIcon="Schedule"
    inactiveIconClass="fb-icon-dark-primary"
    popoverHeight="440px"
    popoverWidth="558px"
    hoverControlHeight="56px"
    hoverControlWidth="310px"
>
    <div class="popover-content" slot="hover-control">
        <div class="increment-buttons">
            {#each Object.keys(steps).sort((keyA, keyB) => parseInt(keyA) - parseInt(keyB)) as key}
                <Button on:click={() => incrementTimestamp(parseInt(key))} disabled={!steps[key]}>
                    {key}
                </Button>
            {/each}
        </div>
    </div>

    <div class="popover-content">
        <div class="datepicker-container">
            {#if $measurementMomentExpandedStore.data}
                <DatePicker
                    enabledTimestamps={$measurementMomentsStore.data?.map((timestamp) => timestamp.moment)}
                    selectedTimestamp={$measurementMomentExpandedStore.data?.moment}
                    on:selected={(ev) => selectDate(ev.detail)}
                />
            {/if}
        </div>

        <div class="scroll-container">
            <div class="scroll-parent">
                {#if timestampOptions && $measurementMomentExpandedStore.data}
                    <Treeview>
                        {#each timestampOptions[formatDateString(toDateOnly($measurementMomentExpandedStore.data.moment))] ?? [] as section}
                            <TreeviewItem
                                    itemToggle
                                    on:treeviewOpen={() => openSection(section)}
                                    opened={section.timestamps.some((timestamp) => datesAreEqual(timestamp, $measurementMomentExpandedStore.data.moment)) || section.opened}
                                    label={section.label}
                            >
                                {#each section.timestamps ?? [] as timestamp, index}
                                    <TreeviewItem
                                            label={formatMomentTime(timestamp)}
                                            selectable
                                            onClick={() => selectMeasurementMoment(timestamp)}
                                            selected={datesAreEqual($measurementMomentExpandedStore.data.moment, timestamp)}
                                    >
                                    <span slot="label-start">
                                        {#if datesAreEqual($measurementMomentExpandedStore.data.moment, timestamp)}
                                            <Icon material="check" />
                                        {:else}
                                            <Icon material="schedule" />
                                        {/if}
                                    </span>
                                    </TreeviewItem>
                                {/each}
                            </TreeviewItem>
                        {/each}
                    </Treeview>
                {/if}
            </div>
        </div>
    </div>
</FloatingButton>

<style lang="scss">
    :global(.popover-inner) {
        height: 100%;
    }

    :global(.fb-icon-dark-primary) {
        color: var(--fab-icon-color);
    }

    .increment-buttons {
        display: flex;
        margin: 8px 8px 0 8px;
    }

    .popover-content {
        display: flex;
        height: 100%;
        flex-direction: row;
    }

    .scroll-parent {
        display: flex;
        flex-flow: column;
        height: 100%;
    }

    .scroll-container {
        background: var(--f7-card-bg-color);
        overflow: auto;
        flex-grow: 1;
    }

    .datepicker-container {
        width: 360px;
        height: 100%;
    }
</style>
